import {
    getProjectsFromFirestore,
    getScreenplaysFromFirestore,
    getStoriesFromFirestore,
} from '@/service/firestore'
import { getScreenplay, getStory } from '@/service/local-db'
import { db, Project, ScreenplayEntity, StoryEntity } from '@/shared/lib/db'

export async function initializeLocalData(userId: string) {
    const projectDocs = await initializeLocalProjects(userId)
    for (const doc of projectDocs || []) {
        initializeLocalScreenplays(userId, doc.id)
        initializeLocalStories(userId, doc.id)
    }
}

async function initializeLocalProjects(userId: string) {
    try {
        // Initialize local projects for the user
        const projectCount = await db.project.where('userId').equals(userId).count()
        if (projectCount === 0) {
            const docs = await getProjectsFromFirestore(userId)
            const projects = docs
                .map((doc) => {
                    const data = doc.data()
                    if (
                        data.title &&
                        data.created_at &&
                        data.updated_at &&
                        data.genre &&
                        data.userId // add check for userId
                    ) {
                        return {
                            id: doc.id,
                            title: data.title,
                            created_at: data.created_at.toDate(),
                            updated_at: data.updated_at.toDate(),
                            genre: data.genre,
                            subGenre: data.subGenre || null,
                            userId: data.userId, // add here
                        } as Project
                    }
                    return null
                })
                .filter((p): p is Project => p !== null)
            await db.project.bulkPut(projects!)
            return docs
        }
    } catch (error) {
        console.error('Error initializing local projects:', error)
        throw new Error('Failed to initialize local projects')
    }
}

async function initializeLocalScreenplays(userId: string, projectId: string) {
    try {
        const screenplayDocs = await getScreenplaysFromFirestore(userId, projectId)

        const screenplay = (
            await Promise.all(
                screenplayDocs.map(async (doc) => {
                    const data = doc.data()
                    const localScreenplay = await getScreenplay(projectId, data.title)
                    if (localScreenplay != null) {
                        return null
                    }
                    if (
                        data.content &&
                        data.created_at &&
                        data.updated_at &&
                        data.title &&
                        data.userId && // add check for userId
                        data.projectId
                    ) {
                        const screenplay = {
                            id: data.projectId + '-' + data.title,
                            content: data.content,
                            created_at: data.created_at.toDate(),
                            updated_at: data.updated_at.toDate(),
                            title: data.title,
                            userId: data.userId, // add here
                            projectId: data.projectId, // add here
                        } as ScreenplayEntity
                        return screenplay
                    }
                    return null
                })
            )
        ).filter((p): p is ScreenplayEntity => p !== null)
        await db.screenplays.bulkPut(screenplay)

        return screenplayDocs
        //}
    } catch (error) {
        console.error('Error initializing local Screenplays:', error)
        throw new Error('Failed to initialize local screenplays')
    }
}

async function initializeLocalStories(userId: string, projectId: string) {
    try {
        const storyDocs = getStoriesFromFirestore(userId, projectId)

        const story = (
            await Promise.all(
                (
                    await storyDocs
                ).map(async (doc) => {
                    const data = doc.data()
                    const localStory = await getStory(projectId, data.title)
                    if (localStory != null) {
                        return null
                    }
                    if (
                        data.content &&
                        data.created_at &&
                        data.updated_at &&
                        data.title &&
                        data.userId &&
                        data.projectId
                    ) {
                        const story = {
                            id: data.projectId + '-' + data.title,
                            content: data.content,
                            created_at: data.created_at.toDate(),
                            updated_at: data.updated_at.toDate(),
                            title: data.title,
                            userId: data.userId,
                            projectId: data.projectId,
                        } as StoryEntity
                        return story
                    }
                    return null
                })
            )
        ).filter((p): p is StoryEntity => p !== null)
        await db.stories.bulkPut(story)
        return storyDocs
    } catch (error) {
        console.error('Error initializing local Stories:', error)
        throw new Error('Failed to initialize local stories')
    }
}
